import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ConversationList from "@/components/chat/ConversationList";
import MessageThread from "@/components/chat/MessageThread";
import NewConversationDialog from "@/components/chat/NewConversationDialog";
import { Button } from "@/components/ui/button";
import { SquarePen } from "lucide-react";

function makeConversationId(idA, idB) {
  return [idA, idB].sort().join("_");
}

export default function Chat() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedConv, setSelectedConv] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const { data: allMessages = [] } = useQuery({
    queryKey: ["chat-messages"],
    queryFn: () => base44.entities.ChatMessage.list("-created_date", 500),
    refetchInterval: 5000,
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users-list"],
    queryFn: () => base44.entities.User.list(),
  });

  // Build conversation summaries involving current user
  const conversations = (() => {
    if (!user) return [];
    const convMap = {};
    allMessages.forEach((msg) => {
      if (msg.sender_id !== user.id && msg.recipient_id !== user.id) return;
      const otherId = msg.sender_id === user.id ? msg.recipient_id : msg.sender_id;
      const cid = msg.conversation_id;
      if (!convMap[cid]) {
        convMap[cid] = { conversation_id: cid, otherId, messages: [], unreadCount: 0 };
      }
      convMap[cid].messages.push(msg);
      if (!msg.read && msg.recipient_id === user.id) {
        convMap[cid].unreadCount++;
      }
    });
    return Object.values(convMap).map((c) => {
      const sorted = [...c.messages].sort((a, b) => new Date(a.created_date) - new Date(b.created_date));
      const otherUser = users.find((u) => u.id === c.otherId);
      return {
        ...c,
        messages: sorted,
        lastMessage: sorted[sorted.length - 1],
        otherUser,
      };
    }).sort((a, b) => {
      const aDate = a.lastMessage?.created_date || 0;
      const bDate = b.lastMessage?.created_date || 0;
      return new Date(bDate) - new Date(aDate);
    });
  })();

  const threadMessages = selectedConv
    ? conversations.find((c) => c.conversation_id === selectedConv.conversation_id)?.messages || []
    : [];

  // Mark messages as read when opening a conversation
  useEffect(() => {
    if (!selectedConv || !user) return;
    const unread = threadMessages.filter((m) => !m.read && m.recipient_id === user.id);
    unread.forEach((m) => base44.entities.ChatMessage.update(m.id, { read: true }));
    if (unread.length > 0) queryClient.invalidateQueries({ queryKey: ["chat-messages"] });
  }, [selectedConv?.conversation_id, threadMessages.length]);

  const handleSend = async (text) => {
    if (!user || !selectedConv) return;
    await base44.entities.ChatMessage.create({
      conversation_id: selectedConv.conversation_id,
      sender_id: user.id,
      sender_name: user.full_name || user.email,
      recipient_id: selectedConv.otherId,
      content: text,
      read: false,
    });
    queryClient.invalidateQueries({ queryKey: ["chat-messages"] });
  };

  const handleSelectUser = (otherUser) => {
    const cid = makeConversationId(user.id, otherUser.id);
    setSelectedConv({ conversation_id: cid, otherId: otherUser.id, otherUser });
  };

  const handleSelectConv = (conv) => {
    setSelectedConv({ ...conv, otherId: conv.otherId, otherUser: conv.otherUser });
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] -mx-4 -mt-4 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      {/* Sidebar — hidden on mobile when a conv is open */}
      <div className={`w-full lg:w-80 border-r border-border flex flex-col ${selectedConv ? "hidden lg:flex" : "flex"}`}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="font-display font-bold text-lg">Messages</h2>
          <Button variant="ghost" size="icon" onClick={() => setShowNew(true)}>
            <SquarePen className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <ConversationList
            conversations={conversations}
            selectedId={selectedConv?.conversation_id}
            onSelect={handleSelectConv}
          />
        </div>
      </div>

      {/* Thread */}
      <div className={`flex-1 flex flex-col ${!selectedConv ? "hidden lg:flex" : "flex"}`}>
        {selectedConv ? (
          <MessageThread
            conversation={selectedConv}
            messages={threadMessages}
            onSend={handleSend}
            onBack={() => setSelectedConv(null)}
          />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2">
            <SquarePen className="w-10 h-10 opacity-30" />
            <p className="text-sm">Select a conversation or start a new one</p>
            <Button variant="outline" size="sm" onClick={() => setShowNew(true)}>New Message</Button>
          </div>
        )}
      </div>

      <NewConversationDialog
        open={showNew}
        onOpenChange={setShowNew}
        onSelect={handleSelectUser}
      />
    </div>
  );
}